import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Input from "../../../../components/common/Input/Input";
import SelectInput from "../../../../components/common/selectInput/selectInput";
import SingleDatePicker from "../../../../components/common/SingleDatePicker/SingleDatePicker";
import TelInput from "../../../../components/common/telInput/telInput";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useLifeFormData from "../../../../hooks/useLifeFormData";
import useValidation from "../../../../hooks/useValidation";
import {
  createLead,
  selectLeadLoader,
} from "../../../../redux/features/expressSheild/leadSlice";
import {
  daysOptions,
  monthsOptions,
  yearsOptions,
} from "../../../../utils/calenderOptions";
import { getClientSource } from "../../../../utils/getClientSource";
import ExpressShieldPlanLayout from "../expressShieldPlanLayout";
import { LEAD_INFO } from "./data";

export const EXPRESS_SHEILD_BASIC_INFO = "EXPRESS_SHEILD_BASIC_INFO";
const BasicInfo = ({ onSuccess }) => {
  const [values, setValues] = useState({});
  const { errors, validateFields } = useValidation();
  const { saveLifeDataInRedux, isInitialized } = useLifeFormData(
    EXPRESS_SHEILD_BASIC_INFO,
    setValues
  );
  const { t } = useTranslation();
  const translate = useLanguageTranslate();
  const handleChange = (e) => {
    const { name, value } = e.target;
    const update = { ...values, [name]: value };
    setValues(update);
    // Only save to Redux after initialization to avoid overwriting restored data
    if (isInitialized.current) {
      saveLifeDataInRedux(update);
    }
  };
  // Mobile uses the phone's native date picker, as the other products do. It
  // hands back YYYY-MM-DD, which splits into the same two-digit day and month
  // and four-digit year the desktop dropdowns store, so validation, the
  // review page and the save payload read it unchanged.
  const handleMobileDateSelect = (dateString) => {
    if (!dateString) return;
    const [year, month, day] = dateString.split("-");
    const update = { ...values, day, month, year };
    setValues(update);
    if (isInitialized.current) {
      saveLifeDataInRedux(update);
    }
  };
  const mobileDateValue =
    values?.year && values?.month && values?.day
      ? `${values.year}-${values.month}-${values.day}`
      : "";
  const validateForm = (data) => {
    let isValid = validateFields(
      [
        {
          name: "email",
          validations: ["required", "email"],
        },
        {
          name: "fullName",
          validations: ["required"],
        },
        {
          name: "day",
          validations: ["select"],
        },
        {
          name: "month",
          validations: ["select"],
        },
        {
          name: "year",
          validations: ["select"],
        },
        {
          name: "mobile",
          validations: ["length"],
          min_length: 8,
        },
      ],
      data ? data : values
    );
    return isValid;
  };
  const dispatch = useDispatch();
  const nextClickHandler = async () => {
    if (validateForm()) {
      saveLifeDataInRedux(values);
      let [FirstName, LastName] = values?.fullName?.split(" ");
      let payload = {
        FirstName,
        LastName,
        CPR: "",
        PhoneNumber: `${values?.countryCode || 973}${values?.mobile}` || "",
        subject: LEAD_INFO.primaryDetails,
        Product: "LIFE",
        Email: "",
        customerType: 1,
        status: 1,
        productType: "Life",
        stage: "",
        lead_id: "",
        Quote: "",
        channel: getClientSource(),
        LOBProduct: "Express Shield Plan",
      };
      const response = await dispatch(createLead(payload));
      if (response?.type == "createLead/fulfilled") {
        onSuccess();
      }
    }
  };
  const isLoading = useSelector(selectLeadLoader);

  return (
    <ExpressShieldPlanLayout
      is_bottom_sticky_layout
      is_hide_back_btn
      btn_onClick={nextClickHandler}
      isLoading={isLoading}
      show_close_btn
    >
      <div className="w-[32%] max767:w-full mx-auto ipad:w-[60%]">
        <TitleDescription
          title={t("buyOnline.expressShieldFlow.basicInfo.title")}
          description={t("buyOnline.expressShieldFlow.basicInfo.description")}
        />
        <Input
          label={t("buyOnline.expressShieldFlow.basicInfo.fullName")}
          type="text"
          name="fullName"
          value={values.fullName}
          error={errors.fullName}
          handleChange={handleChange}
        />
        <Input
          label={t("buyOnline.expressShieldFlow.basicInfo.emailAddress")}
          type="email"
          name="email"
          value={values.email}
          error={errors.email}
          handleChange={handleChange}
        />
        <TelInput
          name="mobile"
          value={values.mobile}
          error={errors.mobile}
          onChange={handleChange}
          label={t("buyOnline.expressShieldFlow.basicInfo.mobileNumber")}
          className="mobilenumber px-4 py-3 max767:w-[50%] w-[97%] border-r-0 border-0 rounded-[9px] h-[46px] focus:outline-none"
        />
        <div className="date_of_birth">
          <label className="block mb-[5px] text-[#48535B] not-italic font-normal leading-6 text-base max-350:text-[13px]">
            {t("buyOnline.expressShieldFlow.basicInfo.dateOfBirth")}
          </label>
          {/* Mobile: native date picker (visible <=767px) */}
          <div className="hidden max767:block">
            <SingleDatePicker
              value={mobileDateValue}
              onChange={handleMobileDateSelect}
              placeholder={translate("Select Date", "اختر التاريخ")}
              error={errors?.day || errors?.month || errors?.year}
              useNativeMobile={true}
            />
          </div>
          {/* Desktop: select dropdowns (visible >767px). Hidden on a plain
              wrapper: index.css has `.date_of_birth .flex { display: flex }`,
              which outranks max767:hidden on the row itself. */}
          <div className="max767:hidden">
          <div className="flex gap-6 self-stretch">
            <SelectInput
              name="day"
              options_data={daysOptions}
              formData={values}
              default_placeholder={t(
                "buyOnline.expressShieldFlow.basicInfo.day"
              )}
              error={errors?.day}
              handleChange={handleChange}
              maxMenuHeight={185}
            />

            <SelectInput
              name="month"
              formData={values}
              default_placeholder={t(
                "motorInsurance.newMotorInsuranceFlow.basicInfo.month"
              )}
              options_data={monthsOptions}
              error={errors?.month}
              handleChange={handleChange}
              maxMenuHeight={185}
            />

            <SelectInput
              name="year"
              formData={values}
              default_placeholder={t(
                "motorInsurance.newMotorInsuranceFlow.basicInfo.year"
              )}
              options_data={yearsOptions}
              error={errors?.year}
              handleChange={handleChange}
              maxMenuHeight={185}
            />
          </div>
          </div>
        </div>
      </div>
    </ExpressShieldPlanLayout>
  );
};
export default BasicInfo;
